// nav_slot — lets a route inject a contextual control into the shared TopBar
// (App.tsx) without the navbar knowing what the control is. The workspace uses
// it to place the Preview|Data toggle in the navbar instead of stealing a strip
// of the content area.
//
// ── TWO PLACEMENTS ──────────────────────────────────────────────────────────
//
// "center" is for controls that belong to what you are LOOKING AT — the view
// switch, the publish button. They sit in the middle, where the page's own
// subject is.
//
// "right" is for controls that sit with the account group: the usage pill, sign
// out. The settings gear lives there because that is where the operator already
// looks for "the controls about me and my app", and because the centre group is
// only rendered once an app has built — the gear must never depend on that.
//
// Two separate contexts on purpose: the SETTER context value is stable (the
// useState setter never changes identity, and the wrapper below is memoized), so
// a route calling useNavSlot never re-renders when a slot node changes — only
// TopBar (which reads the NODE context) does. A single {node,setNode} context
// would couple them and, with an effect that writes the node, spin a render loop.

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";

/** Where in the navbar a route's control goes. */
export type NavPlacement = "center" | "right";

type Slots = Record<NavPlacement, ReactNode>;
const EMPTY: Slots = { center: null, right: null };

const NodeCtx    = createContext<Slots>(EMPTY);
const SetNodeCtx = createContext<(placement: NavPlacement, n: ReactNode) => void>(() => {});

export function NavSlotProvider({ children }: { children: ReactNode }) {
  const [slots, setSlots] = useState<Slots>(EMPTY);
  // Memoized and written through a functional update: the identity stays stable across renders,
  // which is what keeps a route that only SETS a slot from re-rendering when another one changes.
  const set = useCallback((placement: NavPlacement, node: ReactNode) => {
    setSlots((prev) => (prev[placement] === node ? prev : { ...prev, [placement]: node }));
  }, []);
  return (
    <SetNodeCtx.Provider value={set}>
      <NodeCtx.Provider value={slots}>{children}</NodeCtx.Provider>
    </SetNodeCtx.Provider>
  );
}

/** TopBar reads this to render whatever the active route injected at `placement` (or nothing). */
export function useNavSlotNode(placement: NavPlacement): ReactNode {
  return useContext(NodeCtx)[placement];
}

// A route sets its navbar control with this; it auto-clears on unmount. Pass a
// MEMOIZED node (useMemo) so the effect only re-runs when the control actually
// changes, not on every render of the host route.
export function useNavSlot(content: ReactNode, placement: NavPlacement = "center"): void {
  const setNode = useContext(SetNodeCtx);
  useEffect(() => {
    setNode(placement, content);
    return () => setNode(placement, null);
  }, [setNode, placement, content]);
}
